'use client';

import type { Data } from '@strapi/types';
import { useTranslations } from '@power-core/toolkit';
import { useState } from 'react';

import { useAppContext } from '@/modules/app-context/use-app-context';
import { useDocumentApi } from '@/modules/document/hooks/use-document-api';
import { getErrorMessage } from '@/modules/utils/get-error-message';
import { generateRedirectUrl } from '@/modules/utils/redirect-url';
import type { HandleClickParams } from '../types';
import { useAuth } from '@power-core/toolkit';

export const useSmartCardsParams = () => {
	const t = useTranslations();
	const { auth } = useAuth();
	const { project, appConfig, releaseNotesStatus } = useAppContext();
	const { createProjectDocument, isPending } = useDocumentApi();
	const isDisabled = isPending || !project;
	const [error, setError] = useState({ message: '', details: '' });

	const handleDocumentAction = async ({ documentTypeName, url, documentName, region }: HandleClickParams) => {
		if (!project?.documentId) throw new Error('Project document ID not found');

		const body: {
			documentTypeName: string | null | undefined;
			name: string;
			region?: Data.Entity<'api::project-document.project-document'>['region'];
		} = {
			documentTypeName,
			name: documentName ?? t('Untitled'),
		};

		if (documentTypeName === 'T_CURVES') {
			body.region = region;
		}

		createProjectDocument
			.mutateAsync({
				body,
				documentId: project?.documentId,
			})
			.then((response) => {
				const { documentId, name } = response.data;

				if (!documentId || !name) throw new Error('Document ID or Document name not found');

				generateRedirectUrl({
					url,
					documentId,
					toolUrl: appConfig.appName,
					user: auth?.data?.user?.email ?? '',
					region,
				});
			})
			.catch((err) => {
				setError({ message: t(getErrorMessage(err)), details: 'title' });
			});
	};

	return {
		isDisabled,
		isPending,
		releaseNotesStatus,
		error,
		handleDocumentAction,
		setError,
		t,
	};
};
